import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useLoaderData } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables';
import { DialogType } from '../../common/enums.js';
import {
  alvZeroMsg,
  cancelMsg,
  catalogCorporateMessageExampleLinkMsg,
  catalogCorporateMessageExampleMsg,
  catalogCorporateMessageHelpMsg,
  catalogCorporateMessageInstructionMsg,
  catalogCorporateMessageLabelMsg,
  catalogCorporateMessageSelectorMsg,
  companysShareOfMonthlyFeeMsg,
  connectDeviceToRegistrationProgramMsg,
  contractPeriodMsg,
  damageInsuranceCoverMsg,
  damageInsuranceMsg,
  deviceEnrollmentProgramAliasHelpMsg,
  deviceEnrollmentProgramAliasInfoMsg,
  deviceEnrollmentProgramMsg,
  deviceToEnrollmentProgramMsg,
  deviceToEnrollmentReadMoreMsg,
  elisaDevicesServiceMsg,
  enrollmentAliasPlaceHolderMsg,
  fieldCantBeEmptyMsg,
  monthMsg,
  nameOfCatalogMsg,
  t,
} from '../../common/i18n';
import { Checkbox } from '../../common/react-hook-form/components/Checkbox.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { TextAreaComponent } from '../../common/react-hook-form/components/TextAreaComponent.js';
import { Name, TextInput } from '../../common/react-hook-form/fields';
import { BillingAccount } from '../../common/react-hook-form/fields/BillingAccount.js';
import { CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE } from '../../common/utils/billingAccountUtils.js';
import { ContractPeriod } from '../../common/utils/catalogUtils.js';
import { CreateContactType } from '../../common/utils/contactUtils.js';
import { deepEqual } from '../../common/utils/objectUtils';
import { Catalog } from '../../generated/api/catalog.js';
import { ContactRole } from '../../generated/api/contactRole.js';
import { EppCategory } from '../../generated/api/eppCategory.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { BillingAccountFieldset } from '../BillingAccount/BillingAccountFieldset.js';
import { DeliveryMethodFieldset } from '../BillingAccount/DeliveryMethodFieldset.js';
import { CreateContactModal } from '../CreateContactModal/CreateContactModal.js';
import { ExternalLink } from '../ExternalLink/ExternalLink';
import { SelectableBillingAccountList } from '../SelectableBillingAccountList/SelectableBillingAccountList';
import type { BillingAccountListLoaderData } from '../../common/loaders';
import type { SelectRadioItem } from '../../common/react-hook-form/components/SelectRadio.js';
import type { ReceiverType } from '../../common/utils/billingAccountUtils.js';
import type { CatalogProduct } from '../../common/utils/catalogUtils.js';
import type { BillChannel } from '../../generated/api/billChannel.js';
import type { BillingAccountsResponse } from '../../generated/api/billingAccountsResponse.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { Contact } from '../../generated/api/contact.js';
import type { ContactsResponse } from '../../generated/api/contactsResponse.js';
import type { VirtualCatalog } from '../../generated/api/virtualCatalog';
import type { BillingAccountCreateFormValues } from '../CreateBillingAccount/CreateBillingAccount.js';
import type { FieldValues } from 'react-hook-form';

import './CatalogConfigurationForm.scss';

export type CatalogDamageInsurance = Array<Exclude<EppCategory, 'PLACEHOLDER'>>;

interface ContactBaseValues {
  name: string;
  productType: 'EPP_RECURRING' | 'RECURRING' | 'ONETIME';
  damageInsurance: CatalogDamageInsurance;
  corporateMessage?: string;
  enrollmentProgramConsent: boolean;
  enrollmentProgramAlias?: string;
  billingAccountId?: string;
  selectedBillingAccountIds?: Array<string>;
  approverMustSelectBillingAccount?: boolean;
}

interface SupportValues {
  isCorporateMessageAdded?: boolean;
  billingAccount?: BillingAccountCreateFormValues;
  billReceiverSelection?: ReceiverType;
}

export interface FormValues extends SupportValues {
  catalog: ContactBaseValues & {
    contractPeriod?: string;
    corporateShare?: string;
  };
  key?: number;
}

export interface ReadyFormValues extends SupportValues {
  catalog: ContactBaseValues & {
    contractPeriod?: number;
    corporateShare?: number;
  };
  removedDiscontinuedDevices?: string[];
  catalogProducts?: Record<string, Array<CatalogProduct>>;
  virtualCatalog?: VirtualCatalog;
}

export interface CatalogConfigurationFormProps {
  defaultValues?: FormValues;
  isEppActive: boolean;
  isRecurringChargeAllowed: boolean;
  isOnetimeChargeAllowed: boolean;
  onSubmit: (values: FormValues) => void;
  cancelHref: string;
  submitButtonMsg?: string;
  mdmId?: string;
  editing?: boolean;
}

const contractPeriodList = (productType: Catalog.ProductTypeEnum) => {
  const periodList = [
    { label: `${ContractPeriod.MONTHS_12} kk`, value: `${ContractPeriod.MONTHS_12}` },
    { label: `${ContractPeriod.MONTHS_24} kk`, value: `${ContractPeriod.MONTHS_24}` },
    { label: `${ContractPeriod.MONTHS_36} kk`, value: `${ContractPeriod.MONTHS_36}` },
  ];

  // Show 48 months period only for EPP
  if (productType === Catalog.ProductTypeEnum.EPP_RECURRING) {
    periodList.push({ label: `${ContractPeriod.MONTHS_48} kk`, value: `${ContractPeriod.MONTHS_48}` });
  }

  return periodList;
};

export interface CatalogConfigurationLoaderData {
  billingAccounts: BillingAccountsResponse;
  billChannels: BillChannel[];
  contacts: ContactsResponse;
  companyInfo: CompanyInfoResponse;
}

const calculateContractPeriod = (fv: FormValues) => {
  if (fv.catalog.productType === Catalog.ProductTypeEnum.ONETIME) {
    return undefined;
  }
  const contractPeriod = fv.catalog.contractPeriod;
  // If this happens, it means that the form is not validated correctly
  if (!contractPeriod) {
    throw new Error('Contract period is required for non-ONETIME product types');
  }

  return parseInt(contractPeriod, 10);
};

const calculateCorporateShare = (fv: FormValues) => {
  const number = parseFloat(fv.catalog.corporateShare || '');
  return fv.catalog.productType === Catalog.ProductTypeEnum.EPP_RECURRING && !isNaN(number)
    ? Math.round(number * 100)
    : undefined;
};

export const mapFormValuesToBeProcessed = (values: FormValues): ReadyFormValues => {
  return {
    catalog: {
      name: values.catalog.name,
      contractPeriod: calculateContractPeriod(values),
      productType: values.catalog.productType,
      corporateShare: calculateCorporateShare(values),
      damageInsurance: values.catalog.damageInsurance,
      corporateMessage: values.isCorporateMessageAdded ? values.catalog.corporateMessage : undefined,
      enrollmentProgramConsent: values.catalog.enrollmentProgramConsent,
      enrollmentProgramAlias: values.catalog.enrollmentProgramConsent
        ? values.catalog.enrollmentProgramAlias || ''
        : '',
      approverMustSelectBillingAccount: values.catalog.approverMustSelectBillingAccount || false,
      billingAccountId: values.catalog.billingAccountId,
      selectedBillingAccountIds: values.catalog.selectedBillingAccountIds || [],
    },

    billingAccount:
      values.catalog.billingAccountId === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE ? values.billingAccount : undefined,
    // Contacts are already created by CreateContactModal, so we don't need to pass them
    billReceiverSelection: values.billReceiverSelection,
  };
};

export const CatalogConfigurationForm = ({
  defaultValues,
  isEppActive,
  isRecurringChargeAllowed,
  isOnetimeChargeAllowed,
  onSubmit,
  cancelHref,
  submitButtonMsg,
  mdmId,
  editing = false,
}: CatalogConfigurationFormProps) => {
  const loaderData = useLoaderData() as CatalogConfigurationLoaderData;
  const methods = useForm<FieldValues>({
    mode: 'all',
    shouldUnregister: true,
    defaultValues: { ...defaultValues, key: 0 },
  });
  const key = methods.watch('key');
  const defaultCatalogBillingAccountId = methods.watch('catalog.billingAccountId');

  const runtimeValues = methods.watch() as FormValues;
  const [currentMdmId, setCurrentMdmId] = useState<string>();
  const [showMultipleBillingAccounts, setShowMultipleBillingAccounts] = useState(false);

  const [contacts, setContacts] = useState<Contact[]>(loaderData.contacts.contacts || []);
  const [createContactModalOpen, setCreateContactModalOpen] = useState(false);
  const [createContactType, setCreateContactType] = useState<CreateContactType>();

  const productTypeList = [
    isEppActive && { label: t.TRE5(elisaDevicesServiceMsg), value: Catalog.ProductTypeEnum.EPP_RECURRING },
    isRecurringChargeAllowed && {
      label: t.OF9U('Devices subject to monthly fee'),
      value: Catalog.ProductTypeEnum.RECURRING,
    },
    isOnetimeChargeAllowed && {
      label: t.XED2('Device subject to one-time fee'),
      value: Catalog.ProductTypeEnum.ONETIME,
    },
  ].filter(i => i);

  useEffect(() => {
    if (!deepEqual(methods.getValues('key'), defaultValues?.key ?? 0)) {
      methods.setValue('key', defaultValues?.key ?? 0);
    }
  }, [defaultValues?.key, methods]);

  useEffect(() => {
    if (!editing) {
      setContacts(loaderData.contacts.contacts || []);
    }
  }, [currentMdmId, editing, loaderData.contacts.contacts]);

  useEffect(() => {
    if (mdmId !== currentMdmId && !editing) {
      methods.setValue('catalog.billingAccountId', '');
      methods.setValue('key', key + 1);
      setCurrentMdmId(mdmId);
    }
  }, [defaultValues?.key, currentMdmId, key, mdmId, methods, editing]);

  useEffect(() => {
    if (!deepEqual(methods.getValues('billingAccount'), defaultValues?.billingAccount)) {
      methods.setValue('billingAccount', defaultValues?.billingAccount);
    }
  }, [defaultValues?.billingAccount, methods]);

  // Show multiple billing accounts if the default values have them
  useEffect(() => {
    if ((defaultValues?.catalog?.selectedBillingAccountIds?.length ?? 0) > 0) {
      setShowMultipleBillingAccounts(true);
    }
  }, [defaultValues?.catalog?.selectedBillingAccountIds]);

  const handleBillingAccountIdsChange = (selectedIds: string[]) => {
    const selected = selectedIds.filter(id => {
      return id !== defaultCatalogBillingAccountId;
    });
    methods.setValue('catalog.selectedBillingAccountIds', selected.length > 0 ? selected : []);
  };

  const openCreateContactModal = (contactType: CreateContactType) => {
    setCreateContactType(contactType);
    setCreateContactModalOpen(true);
  };

  const handleContactCreated = (contact: Contact) => {
    setCreateContactModalOpen(false);
    const isDuplicate = contacts?.some(c => c.contactId === contact.contactId);
    if (!isDuplicate) {
      setContacts([...(contacts || []), contact]);
    }
    if (createContactType === CreateContactType.BILLING) {
      methods.setValue('billingAccount.billingContactId', contact.contactId!, { shouldValidate: true });
    } else if (createContactType === CreateContactType.BILL_RECEIVER) {
      methods.setValue('billingAccount.billReceiverId', contact.contactId!, { shouldValidate: true });
    }
    methods.setValue('key', (key || 0) + 1);
    setCreateContactType(undefined);
  };

  const handleCreateContactModalClose = () => {
    setCreateContactModalOpen(false);
    setCreateContactType(undefined);
  };

  const { billingAccounts } = useLoaderData() as BillingAccountListLoaderData;
  return (
    <>
      <FormProvider {...methods}>
        <form className="catalog-configuration" onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <Name
            name="catalog.name"
            label={t.M6TP(nameOfCatalogMsg)}
            placeholder={t.M6TP(nameOfCatalogMsg)}
            maxLength={65}
            hint={t.WYPE('Max 65 characters')}
            allowSpecialCharacters={true}
            className="width-50"
          />

          <SelectRadio
            name="catalog.productType"
            label={t.LQ5X('Product types')}
            items={productTypeList as SelectRadioItem[]}
            onChange={() => methods.setValue('catalog.contractPeriod', '24')}
          />
          <SelectRadio
            name="catalog.contractPeriod"
            label={t.ULI0(contractPeriodMsg)}
            items={contractPeriodList(runtimeValues.catalog?.productType)}
            disabled={runtimeValues.catalog?.productType === Catalog.ProductTypeEnum.ONETIME}
            required={runtimeValues.catalog?.productType !== Catalog.ProductTypeEnum.ONETIME}
          />

          {isEppActive && (
            <div className="flex">
              <TextInput
                label={`${t.H8Q4(companysShareOfMonthlyFeeMsg)} (${t.S8TX(alvZeroMsg)})`}
                min={0}
                name="catalog.corporateShare"
                required={false}
                className="width-50"
                type="number"
                placeholder=""
                disabled={runtimeValues.catalog?.productType !== Catalog.ProductTypeEnum.EPP_RECURRING}
              >
                <span className="extension">€/{t.XXVX(monthMsg)}</span>
              </TextInput>
            </div>
          )}

          <h4>{t.QSXP(damageInsuranceMsg)}</h4>
          <p>{t.FLLT(damageInsuranceCoverMsg)}</p>

          <Checkbox
            name="catalog.damageInsurance"
            label="Business Pro"
            value={EppCategory.BUSINESS_PRO}
            disabled={runtimeValues.catalog?.productType !== Catalog.ProductTypeEnum.EPP_RECURRING}
          />
          <Checkbox
            name="catalog.damageInsurance"
            label="Business Premium"
            value={EppCategory.BUSINESS_PREMIUM}
            disabled={runtimeValues.catalog?.productType !== Catalog.ProductTypeEnum.EPP_RECURRING}
          />

          <h4>{t.C8DA(catalogCorporateMessageLabelMsg)}</h4>
          <p>{t.Q57Q(catalogCorporateMessageInstructionMsg)}</p>
          <CL.Button
            className={dsClass.MARGIN_BOTTOM_3}
            color="light"
            onClick={() => {
              openDialog({
                body: <>{t.Y0HD(catalogCorporateMessageExampleMsg)}</>,
                header: '',
                type: DialogType.GENERIC_INFO_DIALOG,
              });
            }}
            size="s"
          >
            {t.ZU62(catalogCorporateMessageExampleLinkMsg)}
          </CL.Button>

          <Checkbox
            className={dsClass.MARGIN_BOTTOM_3}
            name="isCorporateMessageAdded"
            label={t.FFQZ(catalogCorporateMessageSelectorMsg)}
          />

          {runtimeValues.isCorporateMessageAdded && (
            <TextAreaComponent
              rows={5}
              maxLength={1000}
              disabled={!runtimeValues.isCorporateMessageAdded}
              hint={t.HQTV(catalogCorporateMessageHelpMsg)}
              name="catalog.corporateMessage"
              validate={(value: string, values: FormValues) => {
                if (!values.isCorporateMessageAdded) {
                  return;
                }
                return value ? undefined : t.VPVR(fieldCantBeEmptyMsg);
              }}
            />
          )}

          <h4>{t.Q03S(deviceEnrollmentProgramMsg)}</h4>
          <p>
            {t.ZIH3(deviceToEnrollmentProgramMsg)}{' '}
            <ExternalLink href={paths.DEVICE_ENROLLMENT}>{t.X2UI(deviceToEnrollmentReadMoreMsg)}</ExternalLink>
          </p>
          <p>{t.CSOC(deviceEnrollmentProgramAliasHelpMsg)}</p>
          <Checkbox
            className={dsClass.MARGIN_BOTTOM_3}
            name="catalog.enrollmentProgramConsent"
            label={t.MNEQ(connectDeviceToRegistrationProgramMsg)}
          />

          {runtimeValues.catalog?.enrollmentProgramConsent && (
            <TextInput
              label={t.IBVP(enrollmentAliasPlaceHolderMsg)}
              name="catalog.enrollmentProgramAlias"
              required={false}
              placeholder={t.IBVP(enrollmentAliasPlaceHolderMsg)}
              tooltip={t.BJRZ(deviceEnrollmentProgramAliasInfoMsg)}
            />
          )}

          <hr className={dsClass.MARGIN_BOTTOM_4} />

          <h3>{t.IXED('Device catalog billing information')}</h3>
          <h4>{t.Y93H('Select default invoicing agreement for device list')}</h4>
          <p>
            {t.SOJT(
              'The selected billing account will be used by default in all orders from this device list. If necessary, the order acceptor can select another billing account instead.'
            )}
          </p>
          <div className={dsClass.MARGIN_BOTTOM_3}>
            <BillingAccount
              isDefault={true}
              billingAccounts={loaderData.billingAccounts.searchResults || []}
              name="catalog.billingAccountId"
            />
            {runtimeValues.catalog.billingAccountId === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE && (
              <>
                <BillingAccountFieldset
                  billChannels={loaderData.billChannels}
                  contacts={contacts}
                  onCreateBillingContact={() => openCreateContactModal(CreateContactType.BILLING)}
                />
                <DeliveryMethodFieldset
                  billChannels={loaderData.billChannels}
                  contacts={contacts}
                  onCreateBillReceiverContact={() => openCreateContactModal(CreateContactType.BILL_RECEIVER)}
                />
              </>
            )}
          </div>
          <hr />
          <h4>{t.LIB5('Billing account additional settings')}</h4>
          <div className="catalog-configuration__bordered-section">
            <Checkbox
              className={dsClass.MARGIN_BOTTOM_2}
              name="catalog.approverMustSelectBillingAccount"
              label={t.RXBO('Billing account is selected separately for each order')}
            />
            <div className="catalog-configuration__bordered-section-note">
              {t.VL1R('The order acceptor must select the billing account each time that they accept an order.')}
            </div>
          </div>
          <div className="catalog-configuration__bordered-section">
            <div>
              <CL.Checkbox
                id="select-restricted-billing-account-list"
                className={dsClass.MARGIN_BOTTOM_2}
                checked={
                  showMultipleBillingAccounts || (defaultValues?.catalog?.selectedBillingAccountIds?.length ?? 0) > 0
                }
                onChange={() => {
                  if (!showMultipleBillingAccounts) {
                    methods.setValue('catalog.selectedBillingAccountIds', []);
                  }
                  setShowMultipleBillingAccounts(!showMultipleBillingAccounts);
                }}
              >
                {t.RP0S('Restricted list of billing accounts')}
              </CL.Checkbox>
            </div>
            <div className="catalog-configuration__bordered-section-note">
              {t.CYUU('Select which billing accounts the order acceptor can choose for orders.')}
            </div>
            {showMultipleBillingAccounts && (
              <div className={classNames(dsClass.MARGIN_TOP_3, dsClass.PADDING_BOTTOM_3)}>
                <SelectableBillingAccountList
                  billingAccounts={billingAccounts}
                  defaultBillingAccountId={defaultCatalogBillingAccountId}
                  defaultSelectedBillingAccountIds={defaultValues?.catalog.selectedBillingAccountIds}
                  handleBillingAccountIdsChange={handleBillingAccountIdsChange}
                />
              </div>
            )}
          </div>

          <CL.ButtonGroup>
            <Link
              to={cancelHref}
              className={`${dsClass.LINK} ${dsClass.LINK_STYLE_LINK_BUTTON} ${dsClass.LINK_BUTTON_COLOR_LINK} ${dsClass.LINK_BUTTON_SIZE_M}`}
            >
              {t.B2V1(cancelMsg)}
            </Link>
            <CL.Button type="submit" loading={methods.formState.isSubmitting}>
              {submitButtonMsg}
            </CL.Button>
          </CL.ButtonGroup>
        </form>
      </FormProvider>
      {createContactModalOpen && (
        <CreateContactModal
          onClose={handleCreateContactModalClose}
          onContactCreated={handleContactCreated}
          mdmId={mdmId}
          roles={[ContactRole.BILLING_CONTACT]}
        />
      )}
    </>
  );
};
